import { findCustomerSegmentationSegments } from '#domain/core/filter/filter.query';
import { CUSTOMER_SEGMENTATION_TYPES, type CustomerSegmentationType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';

const { CONSOSTYLE, LIFESTAGE } = CUSTOMER_SEGMENTATION_TYPES;

/**
 * Returns `[]` when all canonical segments are selected (≡ no filter), otherwise passes the selection through.
 * Reason: a Looker `IN` filter on every valid segment still drops "Others" (NULL keys), so "all checked"
 * must be coerced to "no filter" to keep totals consistent.
 */
const useNormalizedMicroFilter = (selected: ComputedRef<string[]>, segmentationType: CustomerSegmentationType) => {
  const { data } = useQuery({
    enabled: computed(() => selected.value.length > 0),
    queryKey: ['customerSegmentationSegments', segmentationType],
    queryFn: () => findCustomerSegmentationSegments({ segmentationType, limit: 100 })
  });

  return computed(() => {
    const total = data.value?.status === 'success' ? (data.value.data.children?.length ?? 0) : 0;
    return total > 0 && selected.value.length === total ? [] : selected.value;
  });
};

type Params = {
  consostyleMicroFilters: ComputedRef<string[]>;
  lifestageMicroFilters: ComputedRef<string[]>;
};

export const useNormalizeSegmentMicroFilters = ({ consostyleMicroFilters, lifestageMicroFilters }: Params) => ({
  normalizedConsostyleMicroFilters: useNormalizedMicroFilter(consostyleMicroFilters, CONSOSTYLE),
  normalizedLifestageMicroFilters: useNormalizedMicroFilter(lifestageMicroFilters, LIFESTAGE)
});
